import React from 'react';

const Table = ({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = 'No data available.',
  pagination = null, // { currentPage, totalPages, onPageChange }
  onRowClick = null,
  rowKey = (row, idx) => row.id || idx,
  bulkSelection = null, // { selected, onSelect, onSelectAll }
  actions = null, // function(row): ReactNode
  className = '',
}) => {
  return (
    <div className={`overflow-x-auto bg-white rounded-lg shadow ${className}`}>
      <table className="w-full">
        <thead className="bg-secondary-50 border-b border-secondary-200">
          <tr>
            {bulkSelection && (
              <th className="px-6 py-3">
                <input
                  type="checkbox"
                  checked={bulkSelection.selected.length === data.length && data.length > 0}
                  onChange={bulkSelection.onSelectAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.accessor || col.label}
                className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider"
                style={col.style}
              >
                {col.label}
              </th>
            ))}
            {actions && <th className="px-6 py-3 text-left text-xs font-medium text-secondary-700 uppercase tracking-wider">Actions</th>}
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-secondary-200">
          {loading ? (
            <tr>
              <td colSpan={columns.length + (bulkSelection ? 1 : 0) + (actions ? 1 : 0)} className="text-center py-8">
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (bulkSelection ? 1 : 0) + (actions ? 1 : 0)} className="text-center py-8 text-text-secondary">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={rowKey(row, idx)}
                className="hover:bg-secondary-50 transition-colors duration-150 cursor-pointer"
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {bulkSelection && (
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={bulkSelection.selected.includes(rowKey(row, idx))}
                      onChange={() => bulkSelection.onSelect(rowKey(row, idx))}
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.accessor || col.label} className="px-6 py-4 text-xs" style={col.cellStyle ? col.cellStyle(row) : undefined}>
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
                {actions && (
                  <td className="px-6 py-4">{actions(row)}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex -space-x-px">
            <button
              onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-1 border border-secondary-300 bg-surface text-text-secondary rounded-l hover:bg-secondary-50 disabled:opacity-50"
            >
              Prev
            </button>
            {[...Array(pagination.totalPages)].map((_, idx) => (
              <button
                key={idx + 1}
                onClick={() => pagination.onPageChange(idx + 1)}
                className={`px-3 py-1 border-t border-b border-secondary-300 bg-surface text-text-secondary hover:bg-secondary-50 ${pagination.currentPage === idx + 1 ? 'font-bold bg-primary-50 text-primary' : ''}`}
              >
                {idx + 1}
              </button>
            ))}
            <button
              onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-3 py-1 border border-secondary-300 bg-surface text-text-secondary rounded-r hover:bg-secondary-50 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default Table; 